import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./SenderRenewalTimelineChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsSendersService from "../RequestsSendersService";
import { SENDERS_FILTER_KEY } from "../sendersDetailsShared";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardResponsiveChartOptions,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    reverseMonitorDashboardTimelineSeries,
} from "../../../chart/monitorDashboardChartKit";

@WithRender
@Component({
    diProvide: [RequestsSendersService],
})
export default class SenderRenewalTimelineChart extends Vue {
    @Inject(RequestsSendersService)
    public requestsSendersService!: RequestsSendersService;

    @Prop({ required: true }) public filter!: any;

    private chart: any = null;
    private canvasId: string = "sender-renewal-timeline-chart-canvas";
    private readonly defaultVisibleMonths: number = 12;
    private readonly viewportWidthPx: number = 550;

    public hasData: boolean = true;
    public isLoading: boolean = false;
    public chartCanvasWidthPx: string = "550px";
    private requestVersion: number = 0;
    private monthKeys: string[] = [];
    private chartValues: number[] = [];

    private readonly launchDateStr: string = "2022-02-01";

    public get chartViewportStyle(): Record<string, string> {
        const showScroll = this.hasData && !this.isLoading;
        return {
            width: "550px",
            maxWidth: "100%",
            height: "100%",
            margin: "0 auto",
            overflowX: showScroll ? "auto" : "hidden",
            overflowY: "hidden",
        };
    }

    public beforeDestroy(): void {
        this.destroyChart();
    }

    public onTitleClick(): void {
        this.navigateToDetails(null);
    }

    private navigateToDetails(barIndex: number | null): void {
        const f = this.filter;
        const sendersPayload = {
            dateFrom: f.dateFrom,
            dateTo: f.dateTo,
            tenantId: f.tenant,
            providerId: f.providerId,
            customerType: f.customerType || 0,
        };

        try {
            sessionStorage.setItem(
                SENDERS_FILTER_KEY,
                JSON.stringify(sendersPayload),
            );
        } catch (e) {
        }

        const query: any = {};
        if (barIndex != null && this.monthKeys.length > barIndex) {
            const parsed = this.parseMonthKey(this.monthKeys[barIndex]);
            if (parsed) {
                query.chartYear = String(parsed.year);
                query.chartMonth = String(parsed.month);
            }
        }

        this.$router.push({
            name: "SenderRenewalTimelineDetails",
            query: query,
        });
    }

    private onChartClick(evt: any, elements: any[]): void {
        if (!elements || !elements.length) {
            return;
        }

        const index = elements[0]._index != null ? Number(elements[0]._index) : -1;
        if (index < 0) {
            return;
        }

        const count = this.chartValues.length > index ? Number(this.chartValues[index]) : 0;
        if (!count || count <= 0) {
            return;
        }

        this.navigateToDetails(index);
    }

    private parseMonthKey(monthKey: string): { year: number; month: number } | null {
        const parts = String(monthKey).split("-");
        if (parts.length !== 3) {
            return null;
        }

        const month = Number(parts[0]);
        const year = Number(parts[2]);
        if (isNaN(month) || isNaN(year) || month < 1 || month > 12) {
            return null;
        }

        return { year: year, month: month };
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const resp = await this.requestsSendersService.GetSendersRenewalTimeline(this.filter);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;
        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.chartCanvasWidthPx = `${this.viewportWidthPx}px`;
            this.destroyChart();
            return;
        }

        const rows =
            resp && resp.result && Array.isArray(resp.result) ? resp.result : [];

        const countsByKey: any = {};
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r || !r.date) continue;
            const key = String(r.date);
            const c = r.count != null ? Number(r.count) : 0;
            countsByKey[key] = (countsByKey[key] ? Number(countsByKey[key]) : 0) + c;
        }

        const fromStr = this.resolveDateFrom();
        const toStr = this.resolveDateTo();
        const fromDate = this.parseYmd(fromStr);
        const toDate = this.parseYmd(toStr);

        const startMonth = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
        const endMonth = new Date(toDate.getFullYear(), toDate.getMonth(), 1);

        const labels: string[] = [];
        const monthKeys: string[] = [];
        const startBound = new Date(startMonth.getTime());
        const endBound = new Date(endMonth.getTime());

        while (startBound.getTime() <= endBound.getTime()) {
            monthKeys.push(this.formatMonthKey(startBound));
            labels.push(this.formatMonthDisplay(startBound));
            startBound.setMonth(startBound.getMonth() + 1);
        }

        const values: number[] = [];
        for (let i = 0; i < monthKeys.length; i++) {
            const key = monthKeys[i];
            values.push(countsByKey.hasOwnProperty(key) ? Number(countsByKey[key]) : 0);
        }

        reverseMonitorDashboardTimelineSeries(labels, values, monthKeys);

        this.monthKeys = monthKeys.slice();
        this.chartValues = values.slice();

        const total = values.reduce(function (s, x) { return s + x; }, 0);
        this.hasData = total > 0;

        this.chartCanvasWidthPx = this.hasData
            ? this.computeCanvasWidth(labels.length)
            : `${this.viewportWidthPx}px`;

        await this.$nextTick();
        this.renderChart(labels, values, monthKeys);
        this.scrollToOldest();
    }

    private resolveDateFrom(): string {
        const s = this.filter && this.filter.dateFrom ? String(this.filter.dateFrom) : "";
        return s.length ? s : this.launchDateStr;
    }

    private resolveDateTo(): string {
        const s = this.filter && this.filter.dateTo ? String(this.filter.dateTo) : "";
        return s.length ? s : this.todayYmd();
    }

    private todayYmd(): string {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }

    private parseYmd(s: string): Date {
        const parts = String(s).split("-");
        if (parts.length !== 3) return new Date();
        const y = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const d = Number(parts[2]);
        return new Date(y, m, d);
    }

    private renderChart(labels: string[], values: number[], monthKeys: string[]): void {
        this.destroyChart();

        if (!this.hasData) return;

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;

        const yMax = this.computeNiceYAxisMax(values);
        const color = getMonitorDashboardChartColor("senderRenewalTimeline");
        const self = this;
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();
        const fontFamily = getMonitorDashboardFontFamily();

        const tooltipTitles: string[] = [];
        for (let i = 0; i < monthKeys.length; i++) {
            tooltipTitles.push(this.formatMonthTooltipTitle(monthKeys[i]));
        }

        this.chart = new Chart(ctx, {
            type: "line",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: "",
                        data: values,
                        borderColor: color,
                        backgroundColor: "transparent",
                        pointBackgroundColor: color,
                        pointBorderColor: color,
                        borderWidth: 3,
                        pointRadius: 4,
                        pointStyle: "rectRot",
                        fill: false,
                        lineTension: 0,
                    },
                ],
            },
            plugins: [
                {
                    afterDatasetsDraw: (chart: any) => {
                        this.drawPointValueLabels(chart);
                    },
                },
            ],
            options: Object.assign({}, baseOpts, {
                onClick: function (evt: any, elements: any[]) {
                    self.onChartClick(evt, elements);
                },
                legend: {
                    display: false,
                },
                scales: {
                    xAxes: [{ gridLines: { display: false } }],
                    yAxes: [
                        {
                            scaleLabel: {
                                display: false,
                            },
                            ticks: {
                                beginAtZero: true,
                                precision: 0,
                                max: yMax,
                                fontColor: "#6b7280",
                                fontFamily: fontFamily,
                            },
                        },
                    ],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    enabled: true,
                    callbacks: {
                        title: function (items: any) {
                            if (!items || !items.length) return "";
                            const idx = items[0].index;
                            return tooltipTitles[idx] ? String(tooltipTitles[idx]) : "";
                        },
                        label: function (item: any, data: any) {
                            const idx = item.index;
                            const lines: string[] = [];
                            let total = 0;
                            for (let i = 0; i < data.datasets.length; i++) {
                                const ds = data.datasets[i];
                                const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
                                const value = isNaN(raw) ? 0 : raw;
                                total += value;
                                const dsLabel = ds && ds.label ? String(ds.label) : String(self.$t("shared.senders"));
                                lines.push(dsLabel + ": " + String(value));
                            }
                            if (data.datasets.length > 1) {
                                lines.push(String(self.$t("monitorDashboard.total")) + ": " + String(total));
                            }
                            return lines;
                        },
                    },
                }),
            }),
        });
    }

    private drawPointValueLabels(chart: any): void {
        const ctx2: any = chart.ctx;
        if (!ctx2 || !chart.data || !chart.data.datasets || chart.data.datasets.length === 0) return;

        const dataset = chart.data.datasets[0];
        const meta = chart.getDatasetMeta(0);
        if (!meta || !meta.data) return;

        ctx2.save();
        ctx2.textAlign = "center";
        ctx2.textBaseline = "bottom";
        ctx2.font = "10px " + getMonitorDashboardFontFamily();
        ctx2.fillStyle = "#6b7280";

        for (let i = 0; i < meta.data.length; i++) {
            const point = meta.data[i];
            if (!point || !point._model) continue;

            const value = Number(dataset.data[i] || 0);
            const x = point._model.x;
            const y = point._model.y - 6;
            ctx2.fillText(String(value), x, y);
        }

        ctx2.restore();
    }

    private formatMonthKey(d: Date): string {
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${mm}-01-${yyyy}`;
    }

    private formatMonthTooltipTitle(monthKey: string): string {
        const parts = String(monthKey).split("-");
        if (parts.length !== 3) return monthKey;
        const monthNum = Number(parts[0]) - 1;
        const year = Number(parts[2]);
        const monthsFull = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];
        const name = monthsFull[monthNum] || "";
        return name + " " + String(year);
    }

    private computeCanvasWidth(totalMonths: number): string {
        if (totalMonths <= this.defaultVisibleMonths) {
            return `${this.viewportWidthPx}px`;
        }

        const width = Math.ceil((totalMonths / this.defaultVisibleMonths) * this.viewportWidthPx);
        return `${width}px`;
    }

    private scrollToOldest(): void {
        const viewport = this.$refs.chartViewport as HTMLElement;
        if (!viewport) return;
        viewport.scrollLeft = 0;
    }

    private formatMonthDisplay(d: Date): string {
        const months = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];
        const m = months[d.getMonth()] || "";
        const yy = String(d.getFullYear()).slice(-2);
        return `${m} ${yy}`;
    }

    private computeNiceYAxisMax(values: number[]): number {
        let maxVal = 0;
        for (let i = 0; i < values.length; i++) {
            const v = Number(values[i]);
            if (v > maxVal) maxVal = v;
        }
        if (maxVal <= 0) return 10;

        if (maxVal <= 10) {
            const step = Math.ceil(maxVal / 5) * 5;
            return step < 5 ? 10 : step;
        }
        if (maxVal <= 100) {
            return Math.ceil(maxVal / 10) * 10;
        }
        return Math.ceil(maxVal / 100) * 100;
    }

}
